import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { locale, translate as t } from "./locale";
import type { ScratchVm } from "./scratch-editor";
import { csrfHeaders, type AuthenticatedSession } from "./session";
import { aiRequestKey, clearAiSuccess, rememberAiSuccess, wasAiRequestSuccessful } from "./ai-success";
import { trackTelemetryAction } from "./telemetry";

type Kind = "sprite" | "costume" | "backdrop";
interface Quota { imageRemaining: number; resetAt: string }

const labels: Record<Kind, string> = {
    sprite: "Wygeneruj duszka",
    costume: "Wygeneruj kostium",
    backdrop: "Wygeneruj tło",
};
const names: Record<Kind, string> = {
    sprite: "Wygenerowany duszek",
    costume: "Wygenerowany kostium",
    backdrop: "Wygenerowane tło",
};
const promptLabels: Record<Kind, string> = {
    sprite: "Opisz duszka",
    costume: "Opisz kostium",
    backdrop: "Opisz tło",
};
const nameLabels: Record<Kind, string> = {
    sprite: "Nazwa duszka",
    costume: "Nazwa kostiumu",
    backdrop: "Nazwa tła",
};

const suggestedName = (kind: Kind, prompt: string): string => {
    const description = prompt.trim().split(/[.!?\n,]/u, 1)[0]
        .replace(/^(?:wygeneruj|stwórz|utwórz|narysuj|zrób|dodaj|generate|create|draw|make|genera|crea|dibuja)\s+/iu, "")
        .replace(/^(?:mi|dla mnie|a|an|the|un|una)\s+/iu, "")
        .replace(/^(?:duszka|duszek|postać przedstawiającą|postać|kostiumu|kostium|tło|sprite|costume|backdrop|fondo)\s*/iu, "")
        .trim();
    const value = description.slice(0, 40).trim();
    return value || t(names[kind]);
};

const pngBytes = (data: string): Uint8Array => Uint8Array.from(atob(data), char => char.charCodeAt(0));
const imageContext = (editor: ScratchVm): object => (window.GUI as unknown as {
    captureAssistantGameProject: (vm: ScratchVm) => { projectContext: object };
}).captureAssistantGameProject(editor).projectContext;

export const GeneratedImageDialog = ({ projectId, session, vm }: {
    projectId?: string;
    session?: AuthenticatedSession;
    vm?: ScratchVm;
}) => {
    const [kind, setKind] = useState<Kind>();
    const [prompt, setPrompt] = useState("");
    const [nameOverride, setNameOverride] = useState<string>();
    const [working, setWorking] = useState(false);
    const [elapsedSeconds, setElapsedSeconds] = useState(0);
    const [status, setStatus] = useState("");
    const [quota, setQuota] = useState<Quota>();
    const closeButton = useRef<HTMLButtonElement>(null);
    const promptInput = useRef<HTMLTextAreaElement>(null);
    const mounted = useRef(true);
    const currentProject = useRef(projectId);
    const currentSession = useRef(session?.user.id);
    const currentVm = useRef(vm);
    const scope = useRef({ projectId, userId: session?.user.id, vm });
    const requestStartedAt = useRef(0);
    useEffect(() => {
        if (!working) return;
        const interval = window.setInterval(() => {
            setElapsedSeconds(Math.floor((Date.now() - requestStartedAt.current) / 1000));
        }, 1000);
        return () => window.clearInterval(interval);
    }, [working]);
    useEffect(() => { currentProject.current = projectId; }, [projectId]);
    useEffect(() => { currentSession.current = session?.user.id; }, [session]);
    useEffect(() => { currentVm.current = vm; }, [vm]);
    useEffect(() => {
        if (scope.current.projectId === projectId && scope.current.userId === session?.user.id && scope.current.vm === vm)
            return;
        if (scope.current.vm) clearAiSuccess(scope.current.vm);
        if (vm) clearAiSuccess(vm);
        scope.current = { projectId, userId: session?.user.id, vm };
        setKind(undefined);
        setPrompt("");
        setNameOverride(undefined);
        setStatus("");
        setQuota(undefined);
    }, [projectId, session?.user.id, vm]);
    useEffect(() => {
        mounted.current = true;
        return () => { mounted.current = false; };
    }, []);
    useEffect(() => {
        const onRequest = (event: Event) => {
            const requested = (event as CustomEvent<{ kind?: Kind }>).detail.kind;
            if (requested !== "sprite" && requested !== "costume" && requested !== "backdrop") return;
            setPrompt("");
            setNameOverride(undefined);
            setStatus("");
            setKind(requested);
        };
        window.addEventListener("scratch-cg:generate-image", onRequest);
        return () => window.removeEventListener("scratch-cg:generate-image", onRequest);
    }, []);
    useEffect(() => {
        if (!kind) return;
        if (session) promptInput.current?.focus();
        else closeButton.current?.focus();
        const onKey = (event: KeyboardEvent) => {
            if (event.key === "Escape" && !working) setKind(undefined);
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [kind, session, working]);
    useEffect(() => {
        if (!kind || !session || (quota && !(Date.parse(quota.resetAt) <= Date.now()))) return;
        let active = true;
        void fetch("/api/ai/quota", { credentials: "same-origin" })
            .then(async response => {
                if (response.ok && active) setQuota(await response.json() as Quota);
            }).catch(() => {});
        return () => { active = false; };
    }, [kind, session, quota]);

    const generate = async () => {
        if (!kind || !session || !vm || working || !prompt.trim()) return;
        const startingProject = projectId;
        const startingUser = session.user.id;
        const targetId = vm.editingTarget?.id;
        if (kind === "costume" && (!targetId || vm.editingTarget?.isStage)) {
            setStatus(t("Wybierz duszka w edytorze."));
            return;
        }
        const requestKey = aiRequestKey(session.user.id, projectId, `image:${kind}`, prompt,
            kind === "costume" ? targetId : undefined);
        const imageName = (nameOverride ?? suggestedName(kind, prompt)).trim() || t(names[kind]);
        const context = imageContext(vm);
        if (wasAiRequestSuccessful(vm, requestKey, context)) {
            trackTelemetryAction("ai_result", { mode: "image", imageKind: kind, result: "rejected", reason: "duplicate" });
            setStatus(t("Generacja dla tego opisu została już wykonana."));
            return;
        }
        requestStartedAt.current = Date.now();
        setElapsedSeconds(0);
        setWorking(true);
        setStatus("");
        let phase: "request" | "response" | "import" = "request";
        let attemptId: string | undefined;
        try {
            const response = await fetch("/api/ai/image", {
                method: "POST",
                credentials: "same-origin",
                headers: { "Content-Type": "application/json", ...csrfHeaders(session) },
                body: JSON.stringify({ kind, prompt: prompt.trim(), locale }),
            });
            attemptId = response.headers.get("X-Scratch-CG-AI-Attempt-ID") ?? undefined;
            phase = "response";
            const result = await response.json().catch(() => ({})) as {
                error?: string;
                image?: { mimeType: string; data: string };
                quota?: Quota;
            };
            if (result.quota) setQuota(result.quota);
            if (!response.ok) {
                trackTelemetryAction("ai_result", {
                    mode: "image", imageKind: kind,
                    result: [400, 401, 403, 429].includes(response.status) ? "rejected" : "failure",
                    reason: "response",
                    httpStatus: String(response.status),
                    ...(attemptId ? { attemptId } : {}),
                });
                setStatus(result.error === "AI_MODERATION_BLOCKED" ?
                    t("Ta prośba nie może zostać zrealizowana. Zmień opis i spróbuj ponownie.") :
                    result.error === "AI_MODERATION_UNAVAILABLE" ?
                        t("Sprawdzenie prośby jest chwilowo niedostępne. Spróbuj ponownie później.") :
                        response.status === 429 ? t("Dzienny limit generacji obrazów został wykorzystany.") :
                            t("Nie udało się wygenerować obrazu."));
                return;
            }
            if (result.image?.mimeType !== "image/png" || !result.image.data)
                throw new Error("IMAGE_INVALID");
            if (!mounted.current || currentProject.current !== startingProject || currentUserChanged() || currentVm.current !== vm ||
                (kind === "costume" && vm.editingTarget?.id !== targetId))
                throw new Error("PROJECT_CHANGED");
            const importer = (window.GUI as unknown as {
                importGeneratedImage: (editor: ScratchVm, imageKind: Kind, bytes: Uint8Array, name: string, id?: string) => Promise<void>;
            }).importGeneratedImage;
            phase = "import";
            await importer(vm, kind, pngBytes(result.image.data), imageName, targetId);
            rememberAiSuccess(vm, requestKey, imageContext(vm));
            trackTelemetryAction("ai_result", {
                mode: "image", imageKind: kind, result: "success", ...(attemptId ? { attemptId } : {}),
            });
            setKind(undefined);
        } catch (error) {
            trackTelemetryAction("ai_result", {
                mode: "image", imageKind: kind, result: "failure",
                reason: error instanceof Error && error.message === "PROJECT_CHANGED" ? "project" : phase,
                ...(attemptId ? { attemptId } : {}),
            });
            setStatus(error instanceof Error && error.message === "PROJECT_CHANGED" ?
                t("Projekt lub wybrany duszek zmienił się. Spróbuj ponownie.") :
                t("Nie udało się dodać wygenerowanego obrazu."));
        } finally {
            setWorking(false);
        }

        function currentUserChanged () { return currentSession.current !== startingUser; }
    };

    if (!kind) return null;
    return createPortal(
        <div className="modal-overlay" onMouseDown={event => { if (event.target === event.currentTarget && !working) setKind(undefined); }} role="presentation">
            <section aria-labelledby="generated-image-title" aria-modal="true" className="import-dialog generated-image-dialog" role="dialog">
                <div className="import-dialog-content">
                    <h2 id="generated-image-title">{t(labels[kind])}</h2>
                    {!session ? <p>{t("Zaloguj się, aby generować obrazy.")}</p> : <>
                        <p>{quota ? t("Pozostało generacji obrazów: {count} dzisiaj", { count: String(quota.imageRemaining) }) : t("Sprawdzanie limitu…")}</p>
                        <label htmlFor="generated-image-prompt">{t(promptLabels[kind])}</label>
                        <textarea id="generated-image-prompt" maxLength={1000} onChange={event => { if (!working) setPrompt(event.target.value); }} readOnly={working} ref={promptInput} value={prompt} />
                        <label htmlFor="generated-image-name">{t(nameLabels[kind])}</label>
                        <input id="generated-image-name" maxLength={40} onChange={event => { if (!working) setNameOverride(event.target.value); }}
                            readOnly={working} type="text" value={nameOverride ?? suggestedName(kind, prompt)} />
                        {status && <p role="status">{status}</p>}
                        <button disabled={working || !prompt.trim() || (quota !== undefined && quota.imageRemaining <= 0)} onClick={() => void generate()} type="button">
                            {working ? t("Generowanie obrazu... ({seconds} s.)", { seconds: String(elapsedSeconds) }) : t("Generuj")}
                        </button>
                    </>}
                    <button className="secondary-button" disabled={working} onClick={() => setKind(undefined)} ref={closeButton} type="button">{t("Zamknij")}</button>
                </div>
            </section>
        </div>, document.body,
    );
};
